import { Component } from '@theme/component';

class NeobrutalistFloatingHeader extends Component {
  #lastScrollY = 0;
  #scrollThreshold = 80;
  #isMobileDrawerOpen = false;
  #isCartDrawerOpen = false;

  connectedCallback() {
    super.connectedCallback();

    // Scroll listener for sticky hide/reveal behavior
    if (this.getAttribute('data-sticky-behavior') === 'scroll-up') {
      window.addEventListener('scroll', this.#handleScroll.bind(this), { passive: true });
    }

    // Attach listeners for Drawer trigger and submenus
    this.addEventListener('click', this.#handleClick.bind(this));

    // Observer to refresh content when drawer opens externally
    const drawer = this.querySelector('.neobrutalist-drawer');
    if (drawer) {
      this._drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const isOpen = drawer.classList.contains('neobrutalist-drawer--open');
            if (isOpen && !this.#isCartDrawerOpen) {
              this.#isCartDrawerOpen = true;
              this.refreshDrawerContent();
            } else if (!isOpen && this.#isCartDrawerOpen) {
              this.#isCartDrawerOpen = false;
            }
          }
        });
      });
      this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);
    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
    }
  }

  #handleScroll() {
    const currentScrollY = window.scrollY;
    
    // Do nothing if drawer is open
    if (this.#isMobileDrawerOpen) return;

    if (currentScrollY < 20) {
      // Near top of the screen: always show
      this.classList.remove('floating-header--hidden');
      this.classList.remove('floating-header--scrolled');
    } else {
      this.classList.add('floating-header--scrolled');

      if (currentScrollY > this.#lastScrollY && currentScrollY > this.#scrollThreshold) {
        // Scrolling down: hide header
        this.classList.add('floating-header--hidden');
      } else if (currentScrollY < this.#lastScrollY) {
        // Scrolling up: reveal header
        this.classList.remove('floating-header--hidden');
      }
    }

    this.#lastScrollY = currentScrollY;
  }

  #handleClick(e) {
    // Hamburger / Close drawer toggle
    const burgerBtn = e.target.closest('.neobrutalist-header__burger');
    if (burgerBtn) {
      e.preventDefault();
      this.#toggleMobileDrawer();
      return;
    }

    // Click outside mobile drawer container to close it
    const drawerBg = e.target.closest('.neobrutalist-header__drawer-overlay');
    if (drawerBg) {
      e.preventDefault();
      this.#closeMobileDrawer();
      return;
    }

    // NEW: Dedicated Close Button inside the drawer
    const closeBtn = e.target.closest('.neobrutalist-header__drawer-close');
    if (closeBtn) {
      e.preventDefault();
      this.#closeMobileDrawer();
      return;
    }

    // Toggle submenus on mobile drawer
    const submenuToggle = e.target.closest('.neobrutalist-header__drawer-toggle');
    if (submenuToggle) {
      e.preventDefault();
      const parentLi = submenuToggle.closest('.neobrutalist-header__drawer-item');
      if (parentLi) {
        parentLi.classList.toggle('neobrutalist-header__drawer-item--expanded');
      }
      return;
    }

    // Cart Trigger click: open cart drawer
    const cartTrigger = e.target.closest('.neobrutalist-header__cart-trigger');
    if (cartTrigger) {
      e.preventDefault();
      this.#openCartDrawer();
      return;
    }

    // Cart Close button click: close cart drawer
    const cartClose = e.target.closest('.neobrutalist-drawer__close');
    if (cartClose) {
      e.preventDefault();
      this.#closeCartDrawer();
      return;
    }

    // Cart Overlay click: close cart drawer
    const cartOverlay = e.target.closest('.neobrutalist-drawer__overlay');
    if (cartOverlay) {
      e.preventDefault();
      this.#closeCartDrawer();
      return;
    }

    // Cart Continue shopping click: close cart drawer
    const cartContinue = e.target.closest('.neobrutalist-drawer__btn--continue');
    if (cartContinue) {
      e.preventDefault();
      this.#closeCartDrawer();
      return;
    }
  }

  #toggleMobileDrawer() {
    if (this.#isMobileDrawerOpen) {
      this.#closeMobileDrawer();
    } else {
      this.#openMobileDrawer();
    }
  }

  #openMobileDrawer() {
    this.#isMobileDrawerOpen = true;
    const drawer = this.querySelector('.neobrutalist-header__drawer');
    const burger = this.querySelector('.neobrutalist-header__burger');

    if (drawer) drawer.classList.add('neobrutalist-header__drawer--open');
    if (burger) {
      burger.classList.add('neobrutalist-header__burger--active');
      burger.setAttribute('aria-expanded', 'true');
    }

    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
  }

  #closeMobileDrawer() {
    this.#isMobileDrawerOpen = false;
    const drawer = this.querySelector('.neobrutalist-header__drawer');
    const burger = this.querySelector('.neobrutalist-header__burger');

    if (drawer) drawer.classList.remove('neobrutalist-header__drawer--open');
    if (burger) {
      burger.classList.remove('neobrutalist-header__burger--active');
      burger.setAttribute('aria-expanded', 'false');
    }

    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }

  /* ---- Cart Drawer ---- */
  #toggleCartDrawer() {
    if (this.#isCartDrawerOpen) {
      this.#closeCartDrawer();
    } else {
      this.#openCartDrawer();
    }
  }

  #openCartDrawer() {
    this.#isCartDrawerOpen = true;
    const mainDrawer = this.querySelector('.neobrutalist-drawer') || document.querySelector('.neobrutalist-drawer');
    if (mainDrawer) {
      this.refreshDrawerContent();
      mainDrawer.classList.add('neobrutalist-drawer--open');
    } else {
      window.location.href = '/cart';
    }
  }

  #closeCartDrawer() {
    this.#isCartDrawerOpen = false;
    const mainDrawer = this.querySelector('.neobrutalist-drawer') || document.querySelector('.neobrutalist-drawer');
    if (mainDrawer) {
      mainDrawer.classList.remove('neobrutalist-drawer--open');
    }
  }

  openDrawer() {
    this.#openCartDrawer();
  }

  closeDrawer() {
    this.#closeCartDrawer();
  }

  formatPrice(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `₹${(cents / 100).toFixed(2)}`;
  }

  async refreshDrawerContent() {
    const drawer = this.querySelector('.neobrutalist-drawer') || document.querySelector('.neobrutalist-drawer');
    if (!drawer) return;

    const body = drawer.querySelector('.neobrutalist-drawer__body');
    const subtotal = drawer.querySelector('.neobrutalist-drawer__subtotal');
    if (!body || !subtotal) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update subtotal
      subtotal.textContent = this.formatPrice(cart.total_price);

      if (cart.items.length === 0) {
        body.innerHTML = `
          <div class="neobrutalist-drawer__empty">
            <div class="neobrutalist-drawer__empty-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="neobrutalist-icon-leaf" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle;">
  <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" fill="currentColor" fill-opacity="0.15"></path>
  <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
</svg></div>
            <p style="font-weight:900;text-transform:uppercase;">Your Bag is Empty!</p>
          </div>
        `;
        return;
      }

      let html = '';
      cart.items.forEach((item) => {
        const itemPrice = this.formatPrice(item.final_line_price);
        const originalPrice =
          item.original_line_price !== item.final_line_price
            ? `<span class="neobrutalist-drawer-item__original-price">${this.formatPrice(
                item.original_line_price
              )}</span>`
            : '';
        const itemImage = item.image
          ? item.image
          : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_120.png';

        html += `
          <div class="neobrutalist-drawer-item" data-item-key="${item.key}">
            <div class="neobrutalist-drawer-item__image-wrapper">
              <img src="${itemImage}" alt="${
          item.product_title
        }" class="neobrutalist-drawer-item__image" width="80" height="80">
            </div>
            <div class="neobrutalist-drawer-item__details">
              <div class="neobrutalist-drawer-item__title-row">
                <h4 class="neobrutalist-drawer-item__title">${item.product_title}</h4>
                <button type="button" class="neobrutalist-drawer-item__remove" data-action="remove" data-key="${
                  item.key
                }" aria-label="Remove item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
              ${item.variant_title ? `<p class="neobrutalist-drawer-item__variant">${item.variant_title}</p>` : ''}
              <div class="neobrutalist-drawer-item__controls">
                <div class="neobrutalist-drawer-item__qty">
                  <button type="button" class="neobrutalist-drawer-item__qty-btn" data-action="dec" data-key="${
                    item.key
                  }">−</button>
                  <input type="number" class="neobrutalist-drawer-item__qty-input" value="${
                    item.quantity
                  }" min="0" data-key="${item.key}">
                  <button type="button" class="neobrutalist-drawer-item__qty-btn" data-action="inc" data-key="${
                    item.key
                  }">+</button>
                </div>
                <div class="neobrutalist-drawer-item__price">
                  ${originalPrice}
                  <span class="neobrutalist-drawer-item__final-price">${itemPrice}</span>
                </div>
              </div>
            </div>
          </div>
        `;
      });

      body.innerHTML = html;
      this.bindDrawerActions(body);
    } catch (err) {
      console.error(err);
    }
  }

  bindDrawerActions(body) {
    body.querySelectorAll('.neobrutalist-drawer-item__qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        const key = btn.dataset.key;
        const input = btn.parentElement.querySelector('.neobrutalist-drawer-item__qty-input');
        let val = parseInt(input.value) || 0;

        if (action === 'inc') {
          val++;
        } else if (action === 'dec' && val > 0) {
          val--;
        }
        input.value = val;
        this.updateCartQuantity(key, val);
      });
    });

    body.querySelectorAll('.neobrutalist-drawer-item__qty-input').forEach((input) => {
      input.addEventListener('change', () => {
        const key = input.dataset.key;
        let val = parseInt(input.value);
        if (isNaN(val) || val < 0) val = 0;
        input.value = val;
        this.updateCartQuantity(key, val);
      });
    });

    body.querySelectorAll('.neobrutalist-drawer-item__remove').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        this.updateCartQuantity(key, 0);
      });
    });
  }

  async updateCartQuantity(key, qty) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: key, quantity: qty }),
      });

      if (!response.ok) throw new Error('Changing cart quantity failed');

      await this.refreshDrawerContent();
      this.updateHeaderBadges();
    } catch (err) {
      console.error(err);
    }
  }

  async updateHeaderBadges() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const badges = document.querySelectorAll('#CartCount, .cart-count-bubble, .header__cart-count, .neobrutalist-header__cart-count');
      badges.forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.classList.remove('hidden');

        badge.style.animation = 'none';
        requestAnimationFrame(() => {
          badge.style.animation = 'neobrutalist-badge-wiggle 0.4s ease-out';
        });
      });
    } catch (err) {
      console.log(err);
    }
  }
}

if (!customElements.get('neobrutalist-floating-header')) {
  customElements.define('neobrutalist-floating-header', NeobrutalistFloatingHeader);
}
